import {
  AfterViewInit,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ValidEmailRegExp } from '@core/constants/patterns';
import { UpdateUserRequest } from '@core/dto/user.dto';
import { LocalStorageKey } from '@core/enums/local-storage-key.enum';
import { Locale } from '@core/enums/locale';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AppState } from '@core/store';
import { selectCurrentConsultant } from '@core/store/consultant';
import { selectRegistrationRequest, selectUser, selectUserLoading } from '@core/store/user';
import { UserModel } from '@core/store/user/user-state-models';
import { fetchUser, updateUser } from '@core/store/user/user.actions';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { emailValidator } from '@shared/utils/email-validator-utils';
import { isMexEnv } from '@shared/utils/environment-utils';
import { b2cPolicies, createB2CUrl } from 'app/auth-config';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SignupSuccessModalComponent } from './signup-success-modal/signup-success-modal.component';

enum profileFormKey {
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
  phoneNumber = 'phoneNumber',
  newsletters = 'newsletters',
  shareWithConsultant = 'shareWithConsultant',
  smsTransactionalConsent = 'smsTransactionalConsent',
  smsPromotionalConsent = 'smsPromotionalConsent',
}

@Component({
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly profileFormKey = profileFormKey;
  readonly OldPasswordRequired = 'oldPasswordRequired';
  readonly showSmsConsent = !isMexEnv;

  loading$: Observable<boolean>;
  user$: Observable<any>;
  disableSave$: Observable<boolean>;
  phoneNumberIsVerified$: Observable<boolean>;
  profileForm: FormGroup;
  userSubscription: Subscription;
  mobileUser!: string;
  txtMessage: string = $localize`Your account has been created`;
  phoneNumberCreated: boolean = false;
  currentConsultant: any;

  @ViewChild('signUpSuccess', { static: true })
  private signupSuccessModal: SignupSuccessModalComponent;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private store$: Store<AppState>,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    @Inject(LOCALE_ID) private localeId: Locale,
  ) {}

  ngOnInit(): void {
    this.loading$ = this.store$.select(selectUserLoading);
    this.route.queryParamMap.subscribe((params) => {
      this.mobileUser = params.get('mobileUser')!;
      this.mobileUser === 'created'
        ? (this.phoneNumberCreated = true)
        : (this.phoneNumberCreated = false);
    });
    this.profileForm = this.fb.group({
      [profileFormKey.firstName]: ['', Validators.required],
      [profileFormKey.lastName]: ['', Validators.required],
      [profileFormKey.email]: ['', [Validators.required, emailValidator(ValidEmailRegExp)]],
      [profileFormKey.phoneNumber]: '',
      contactMethod: [''],
      [profileFormKey.newsletters]: false,
      [profileFormKey.shareWithConsultant]: true,
      [profileFormKey.smsTransactionalConsent]: false,
      [profileFormKey.smsPromotionalConsent]: false,
    });

    this.store$.dispatch(fetchUser());
    this.user$ = this.store$.select(selectUser);
    this.disableSave$ = this.store$.select(selectUserLoading);
    this.userSubscription = this.user$.subscribe((user) => {
      this.updateForm(user);
    });
    this.phoneNumberIsVerified$ = combineLatest([
      this.profileForm.get(profileFormKey.phoneNumber).valueChanges,
      this.user$,
    ]).pipe(
      map(
        ([value, user]) =>
          (user.phoneNumberValidated && user.phoneNumber === value) || this.phoneNumberCreated,
      ),
    );
  }

  get showChangePassword(): boolean {
    return (this.profileForm.get('contactMethod')?.value ?? '').toLowerCase() === 'email';
  }

  ngAfterViewInit() {
    if (this.mobileUser != undefined) {
      if (this.mobileUser === 'created') {
        this.txtMessage = $localize`Your account has been created.`;
        setTimeout(() => this.signupSuccessModal.open());

        this.subscriptions.add(
          combineLatest([
            this.store$.select(selectRegistrationRequest),
            this.store$.select(selectCurrentConsultant),
          ])
            .pipe(take(1))
            .subscribe(([res, consultant]) => {
              const usr: UpdateUserRequest = {
                firstName: res.firstName,
                lastName: res.lastName,
                email: res.email,
                phoneNumber: res.phoneNumber,
                phoneNumberValidated: true,
                newsletters: res.newsletters,
                updates: res.shareWithConsultant,
                contactMethod: 'phone',
                activeConsultantProwessId: consultant?.beeNumber,
                smsTransactionalConsent: false,
                smsPromotionalConsent: false,
              };
              // to get the consultant details
              this.currentConsultant = consultant;
              this.currentConsultant = this.store$.dispatch(updateUser({ user: usr }));
            }),
        );
      } else if (this.mobileUser === 'failed') {
        this.txtMessage = $localize`Failed to verify phone number.`;
        setTimeout(() => this.signupSuccessModal.open());
      }
    }
  }

  get showPhoneVerificationPart(): boolean {
    return (
      !!this.profileForm.get(this.profileFormKey.phoneNumber).value &&
      environment.feature.mobileLogin
    );
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  onCancel = (): void => {
    this.store$.dispatch(fetchUser());
  };

  onSave = (): void => {
    if (this.profileForm.valid) {
      this.store$
        .select(selectUser)
        .pipe(take(1))
        .subscribe((user) => {
          const formValue = this.profileForm.value;
          const now = new Date().toISOString();
          console.log('localeId:', this.localeId);
          this.store$.dispatch(
            updateUser({
              user: {
                ...formValue,
                languagePreference: this.localeId,
                activeConsultantProwessId: this.currentConsultant?.beeNumber,
                smsTransactionalConsentUpdatedOn:
                  formValue.smsTransactionalConsent !== user.smsTransactionalConsent
                    ? now
                    : user.smsTransactionalConsentUpdatedOn,
                smsPromotionalConsentUpdatedOn:
                  formValue.smsPromotionalConsent !== user.smsPromotionalConsent
                    ? now
                    : user.smsPromotionalConsentUpdatedOn,
              },
            }),
          );
        });
    }
  };

  changePassword(): void {
    const email = this.profileForm.get(profileFormKey.email).value;
    const redirectUri = `${window.location.origin}${window.location.pathname}`;

    const passwordChangeUrl = createB2CUrl(
      b2cPolicies.names.profileEditPasswordChange,
      this.localeId,
      redirectUri,
    );

    if (email) {
      window.location.href = `${passwordChangeUrl}&prefill_email=${email}`;
    }
  }

  verifyPhoneNumber(): void {
    const phoneNumber = this.profileForm.get(profileFormKey.phoneNumber).value;
    if (!phoneNumber) return;

    const email = this.profileForm.get(profileFormKey.email).value;
    const redirectUri = `${window.location.origin}${window.location.pathname}`;

    const verifyPhoneNumberUrl = createB2CUrl(
      b2cPolicies.names.verifyPhoneNumber,
      this.localeId,
      redirectUri,
    );

    this.localStorageService.setItem(LocalStorageKey.toVerifyPhoneNumber, phoneNumber);
    // eslint-disable-next-line max-len
    window.location.href = `${verifyPhoneNumberUrl}&prefill_phone=${phoneNumber}&prefill_email=${email}`;
  }

  updateForm = (user: UserModel): void => {
    this.profileForm.patchValue(user);
  };

  validInput = (controlName: string): boolean => {
    const formControl = this.profileForm.get(controlName);
    if (!formControl.touched) {
      return true;
    }
    return formControl.valid;
  };
}
